import type { GoogleAnalyticsConverter } from '@maestro/google-analytics/google-analytics-converter'

import { PageType } from '@maestro/core/types/global.types'
import { type CartEntry, EventType, type Product } from '@maestro/data-layer'

export interface GAmoduleConfig {
  isEnabled: boolean
  gtmId: string
  gtmUrlParams: string
  isDebug: boolean
  preconnectUrls: string[]
}

export interface GAproviderProps {
  config?: GAmoduleConfig
}

export interface GAeventSubscriberProps {
  children?: React.ReactNode
  config?: GAmoduleConfig
  converter?: GoogleAnalyticsConverter
  pageTypeMapping?: GaPageTypeMapping
}

export type GtagFunction = (...args: unknown[]) => void

declare global {
  interface Window {
    gtag: GtagFunction
    dataLayer: Record<string, unknown>[]
  }
}

export enum GApageType {
  ACCOUNT = 'account',
  ARTICLE = 'article',
  ARTICLE_LIST = 'article_list',
  BRAND = 'brand',
  CATEGORY = 'category',
  CAMPAIGN = 'campaign',
  CART = 'cart',
  CHECKOUT = 'checkout',
  CMS = 'cms',
  CUSTOMER_SERVICE = 'customer_service',
  HOMEPAGE = 'home',
  NOT_FOUND = 'not_found',
  ORDER_CONFIRMATION = 'order_confirmation',
  PRODUCT = 'product',
  SEARCH = 'search',
  STORE_PAGES = 'store_pages',
  OTHER = 'other',
  WISHLIST = 'wishlist',
}

export type GaPageViewParams = {
  page_type: GApageType | string
} & Record<string, unknown>

export type GaPageTypeMapping = Partial<
  Record<string, GApageType | string | GaPageViewParams>
>

export enum GAeventName {
  PAGE_VIEW = 'page_view',

  // Ecommerce standart events
  SEARCH = 'search',
  VIEW_ITEM_LIST = 'view_item_list',
  SELECT_ITEM = 'select_item',
  VIEW_ITEM = 'view_item',
  VIEW_PROMOTION = 'view_promotion',
  SELECT_PROMOTION = 'select_promotion',
  ADD_TO_CART = 'add_to_cart',
  ADD_TO_WISHLIST = 'add_to_wishlist',
  REMOVE_FROM_CART = 'remove_from_cart',
  VIEW_CART = 'view_cart',
  BEGIN_CHECKOUT = 'begin_checkout',
  ADD_SHIPPING_INFO = 'add_shipping_info',
  ADD_PAYMENT_INFO = 'add_payment_info',
  PURCHASE = 'purchase',

  // Additional events
  TOP_NAVIGATION = 'top_navigation',
  MAIN_NAVIGATION_MENU = 'menu_click',
  SUB_MENU = 'sub_menu',
  SORT = 'sort',
  FILTER = 'filter',
  COLLECT_AT_STORE = 'collect_at_store',
  STORE_SELECTED = 'store_selected',
  SLIDE_IN = 'slide_in',
  CREATE_ACCOUNT = 'sign_up',
  JOIN_CLUB = 'join_club',
  LOGIN = 'login',
  LOGOUT = 'logout',
  LEAVE_CLUB = 'leave_club',

  FOOTER_LINK_CLICKED = 'footer_click',
  SEARCH_SUGGESTIONS_CLICKED = 'search_interaction',
  CTA_CLICKED = 'cta_click',
  SEARCH_ARTICLES_VIEWED = 'virtual_page_vie',
  BREADCRUMBS_CLICKED = 'breadcrumb_click',

  ALERT_SHOWN = 'notification',
  ACCORDION = 'accordion',
  CART_SCROLL_COMPLETE = 'cart_scroll_complete',

  HELPBOX = 'helpbox',
  ERROR_MESSAGE = 'error_message',
  EMPTY_CART = 'empty_cart',
  BALL_MENU = 'ball_menu',
}

export const GA_EVENT_MAPPING: Partial<
  Record<EventType, GAeventName | string>
> = {
  [EventType.ACCORDION]: GAeventName.ACCORDION,
  [EventType.ALERT_SHOWN]: GAeventName.ALERT_SHOWN,
  [EventType.BALL_MENU_CLICKED]: GAeventName.BALL_MENU,
  [EventType.BREADCRUMBS_CLICKED]: GAeventName.BREADCRUMBS_CLICKED,
  [EventType.CART_SCROLL_COMPLETE]: GAeventName.CART_SCROLL_COMPLETE,
  [EventType.CART_VIEWED]: GAeventName.VIEW_CART,
  [EventType.CHECKOUT_STARTED]: GAeventName.BEGIN_CHECKOUT,
  [EventType.COLLECT_AT_STORE_ORDER_COMPLETED]: GAeventName.PURCHASE,
  [EventType.CTA_CLICKED]: GAeventName.CTA_CLICKED,
  [EventType.ERROR_MESSAGE]: GAeventName.ERROR_MESSAGE,
  [EventType.FOOTER_LINK_CLICKED]: GAeventName.FOOTER_LINK_CLICKED,
  [EventType.JOINED_CLUB]: GAeventName.JOIN_CLUB,
  [EventType.HELPBOX]: GAeventName.HELPBOX,
  [EventType.LEFT_CLUB]: GAeventName.LEAVE_CLUB,
  [EventType.MENU_CLICKED]: GAeventName.MAIN_NAVIGATION_MENU,
  [EventType.ORDER_COMPLETED]: GAeventName.PURCHASE,
  [EventType.PAGE_VIEW]: GAeventName.PAGE_VIEW,
  [EventType.PAYMENT_INFO_ADDED]: GAeventName.ADD_PAYMENT_INFO,
  [EventType.PRODUCT_ADDED_TO_CART]: GAeventName.ADD_TO_CART,
  [EventType.PRODUCT_ADDED_TO_WISHLIST]: GAeventName.ADD_TO_WISHLIST,
  [EventType.PRODUCT_CLICKED]: GAeventName.SELECT_ITEM,
  [EventType.PRODUCT_LIST_VIEWED]: GAeventName.VIEW_ITEM_LIST,
  [EventType.PRODUCT_REMOVED_FROM_CART]: GAeventName.REMOVE_FROM_CART,
  [EventType.PRODUCT_VIEWED]: GAeventName.VIEW_ITEM,
  [EventType.PRODUCTS_FILTERED]: GAeventName.FILTER,
  [EventType.PRODUCTS_SORTED]: GAeventName.SORT,
  [EventType.PROMOTION_SELECTED]: GAeventName.SELECT_PROMOTION,
  [EventType.PROMOTION_VIEWED]: GAeventName.VIEW_PROMOTION,
  [EventType.REMOVED_ALL_PRODUCTS_FROM_CART]: GAeventName.EMPTY_CART,
  [EventType.SEARCH_ARTICLES_VIEWED]: GAeventName.SEARCH_ARTICLES_VIEWED,
  [EventType.SEARCH_SUGGESTIONS_CLICKED]:
    GAeventName.SEARCH_SUGGESTIONS_CLICKED,
  [EventType.SEARCH]: GAeventName.SEARCH,
  [EventType.SHIPPING_INFO_ADDED]: GAeventName.ADD_SHIPPING_INFO,
  [EventType.SLIDE_IN_OPENED]: GAeventName.SLIDE_IN,
  [EventType.STORE_SELECTED]: GAeventName.STORE_SELECTED,
  [EventType.SUB_MENU_CLICKED]: GAeventName.SUB_MENU,
  [EventType.TOP_NAVIGATION_CLICKED]: GAeventName.TOP_NAVIGATION,
  [EventType.USER_LOGGED_IN]: GAeventName.LOGIN,
  [EventType.USER_LOGGED_OUT]: GAeventName.LOGOUT,
  [EventType.USER_SIGNED_UP]: GAeventName.CREATE_ACCOUNT,
}

export const GA_PAGE_MAPPING: Partial<Record<PageType, GApageType | string>> = {
  [PageType.ACCOUNT_INFORMATION]: GApageType.ACCOUNT,
  [PageType.ARTICLE]: GApageType.ARTICLE,
  [PageType.ARTICLE_CATEGORY]: GApageType.ARTICLE_LIST,
  [PageType.BRAND]: GApageType.BRAND,
  [PageType.BRANDS_OVERVIEW]: GApageType.BRAND,
  [PageType.CART]: GApageType.CART,
  [PageType.CATEGORY]: GApageType.CATEGORY,
  [PageType.CHECKOUT]: GApageType.CHECKOUT,
  [PageType.CHECKOUT_SUCCESS]: GApageType.ORDER_CONFIRMATION,
  [PageType.FIND_STORE]: GApageType.OTHER,
  [PageType.FIND_STORE_LIST]: GApageType.OTHER,
  [PageType.HOMEPAGE]: GApageType.HOMEPAGE,
  [PageType.LOGIN]: GApageType.ACCOUNT,
  [PageType.MY_ACCOUNT]: GApageType.ACCOUNT,
  [PageType.NOT_FOUND]: GApageType.NOT_FOUND,
  [PageType.OFFERS]: GApageType.ACCOUNT,
  [PageType.ORDER]: GApageType.ACCOUNT,
  [PageType.ORDERS]: GApageType.ACCOUNT,
  [PageType.PAGE]: GApageType.CMS,
  [PageType.PRODUCT]: GApageType.PRODUCT,
  [PageType.REGISTER]: GApageType.ACCOUNT,
  [PageType.RESET_PASSWORD]: GApageType.OTHER,
  [PageType.SEARCH]: GApageType.SEARCH,
  [PageType.WISHLIST]: GApageType.ACCOUNT,
}

export interface EventMapping {
  eventType: EventType
  gaEventName: GAeventName | string
  subscriberId?: string
}

export const GA_EVENT_MAPPINGS: EventMapping[] = Object.entries(
  GA_EVENT_MAPPING
).map(([eventType, gaEventName]) => ({
  eventType: eventType as EventType,
  gaEventName,
}))

export interface GoogleAnalyticsItem {
  item_id: string
  item_name: string
  affiliation?: string
  item_variant?: string
  item_brand?: string
  price: number
  currency: string
  quantity: number
  item_category?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_category5?: string
  index?: number
  item_list_id?: string
  item_list_name?: string
  item_list_type?: string
  discount?: number
  on_sale?: string
  coupon?: string
  campaign_label?: string
  campaign_type?: string
  availability_online?: string
  availability_in_stores?: string
  club_offer?: string
  item_type?: string
}

export interface ProductItemInput {
  product: Product
  quantity: number
  variantId?: string
  position?: number
  coupon?: string
}

export interface CartItemInput {
  product: Product
  quantity: number
  variantId?: string
  coupon?: string
  atcType?: string
  affiliation?: string
  listId?: string
  listName?: string
  position?: number
}

export interface TransactionItemInput {
  product: Product
  quantity: number
  variantId?: string
  coupon?: string
  queryID?: string
  listId?: string
  listName?: string
}

export interface ListInfo {
  listId?: string
  listName: string
}

export interface TransactionData {
  value: number
  tax: number
  shipping: number
  currency: string
  transactionId?: string
  orderLevelCoupon?: string
  shippingTier: string
  paymentType: string
  additionalData?: Record<string, unknown>
  deliveryMethod?: string
  checkoutMethod?: string
  cartSize?: number
}

export interface CartEventOptions {
  orderLevelCoupon?: string
  additionalData?: Record<string, unknown>
  shippingTier?: string
  paymentType?: string
  deliveryMethod?: string
  checkoutMethod?: string
  cartSize?: number
  currency?: string
  value?: number
  cartItems?: CartEntry[]
}

export interface SingleItemEventOptions {
  listId?: string
  listName?: string
  position?: number
}

export interface ConvertProductOptions {
  quantity?: number
  variantId?: string
  index?: number
  listId?: string
  listName?: string
  listType?: string
  affiliation?: string
}

export interface GoogleAnalyticsEventData {
  // Search events
  search_term?: string
  number_of_results?: number

  // Sorting and filtering
  sort_by?: string
  filter_name?: string
  filter_by?: string

  // List information
  item_list_id?: string
  item_list_name?: string

  // Transaction data
  value?: number
  tax?: number
  shipping?: number
  currency?: string
  transaction_id?: string
  coupon?: string
  shipping_tier?: string
  payment_type?: string

  // Items array
  items?: GoogleAnalyticsItem[]

  // Authentication and search service
  authenticatedUserToken?: string
  searchServiceIndex?: string

  // Step information
  step?: string

  // Navigation
  item_clicked?: string
  level1?: string
  level2?: string
  level3?: string

  // Slide-in data
  slide_in_type?: unknown

  // Position
  index?: number

  // Promotion data
  creative_name?: string
  creative_slot?: string
  promotion_id?: string
  promotion_name?: string

  footer_section_title?: string
  link_text?: string
  click_text?: string
  url?: string

  search_section?: string
  name?: string
  location?: string

  item_type?: string // rx, otc, para
  campaign_label?: string // campaign label e.g. 2 for 1

  // Generic catch-all for any additional properties
  [key: string]: unknown
}
